import React, {useState} from "react";
import {parseFieldsList} from "../utils/format";
import Tag from "./Tag";
import "./TagsInput.css";

/**
 * Edits a comma-separated list as removable tags. Type a name and press
 * Enter or comma (or leave the box) to add it; Backspace on an empty box
 * removes the last tag; pasting "a, b, c" adds all three.
 *
 * The value stays the API's plain comma-separated string.
 *
 * @param {{
 *   id: string,
 *   value: string,
 *   onChange: (value: string) => void,
 *   maxLength?: number,
 *   invalid?: boolean,
 *   placeholder?: string,
 * }} props
 */
const TagsInput = ({id, value, onChange, maxLength, invalid = false, placeholder}) => {
    const tags = parseFieldsList(value);
    const [draft, setDraft] = useState("");

    const emit = (next) => onChange(next.join(","));

    // Adds every name in `text` that isn't a duplicate and still fits.
    const add = (text) => {
        const next = [...tags];
        for (const name of text
            .split(",")
            .map((n) => n.trim())
            .filter(Boolean)) {
            if (next.includes(name)) continue;
            if (maxLength && [...next, name].join(",").length > maxLength) continue;
            next.push(name);
        }
        if (next.length !== tags.length) emit(next);
    };

    const commit = () => {
        if (draft.trim()) add(draft);
        setDraft("");
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === ",") {
            // Enter must not submit the surrounding form.
            event.preventDefault();
            commit();
        } else if (event.key === "Backspace" && !draft && tags.length) {
            emit(tags.slice(0, -1));
        }
    };

    const handleChange = (event) => {
        const text = event.target.value;
        // Pasted text with commas is split right away.
        if (text.includes(",")) {
            add(text);
            setDraft("");
        } else {
            setDraft(text);
        }
    };

    return (
        <div
            className={`tags-input form-control d-flex flex-wrap align-items-center gap-1${invalid ? " is-invalid" : ""}`}
            onClick={(event) => event.currentTarget.querySelector("input")?.focus()}
        >
            {tags.map((name) => (
                <Tag key={name} onRemove={() => emit(tags.filter((t) => t !== name))}>
                    {name}
                </Tag>
            ))}
            <input
                id={id}
                value={draft}
                placeholder={tags.length ? undefined : placeholder}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={commit}
            />
        </div>
    );
};

export default TagsInput;
